import { Injectable, computed, signal, effect } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { PlaceHolder } from "@app/interface/interfaces";
import { fromEvent } from "rxjs";
import { filter } from "rxjs/operators";

// Define interfaces for your data
interface UserData {
  id?: string;
  name?: string;
  email?: string;
  preferences?: Record<string, any>;
  lastAccessed?: string;
  // Add other user properties as needed
}

interface MigrationState {
  loading: boolean;
  userData: UserData;
  currentMigrationId?: string;
  migrationProgress?: number;
  placeHolders?: PlaceHolder[];

  // Add other migration-specific state as needed
}

@Injectable({
  providedIn: "root",
})
export class MigrationStore {
  // Storage keys
  private readonly STORAGE_KEY = "migrationStoreState";

  // Initialize state from localStorage if available
  private state = signal<MigrationState>({
    loading: false,
    userData: JSON.parse(localStorage.getItem("userData") || "{}"),
    migrationProgress: 0,
  });

  // Computed values (derived state)
  public readonly isLoading = computed(() => this.state().loading);
  public readonly userData = computed(() => this.state().userData);
  public readonly userName = computed(() => this.state().userData?.name);
  public readonly userEmail = computed(() => this.state().userData?.email);
  public readonly userPreferences = computed(
    () => this.state().userData?.preferences || {},
  );
  public readonly currentMigrationId = computed(
    () => this.state().currentMigrationId,
  );
  public readonly placeHolders = computed(() => this.state().placeHolders);

  constructor() {
    // Initialize from localStorage if available
    const savedState = localStorage.getItem(this.STORAGE_KEY);
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        this.state.set(parsedState);
      } catch (e) {
        console.error("Failed to parse saved state:", e);
      }
    }

    // Sync state to localStorage whenever it changes
    effect(() => {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.state()));
    });

    // Listen for changes from other tabs
    fromEvent<StorageEvent>(window, "storage")
      .pipe(
        takeUntilDestroyed(),
        filter(
          (event) => event.key === this.STORAGE_KEY && event.newValue !== null,
        ),
      )
      .subscribe((event) => {
        try {
          // Update local state when another tab changes storage
          const newState = JSON.parse(event.newValue!);
          this.state.set(newState);
        } catch (e) {
          console.error("Failed to parse state from storage event:", e);
        }
      });
  }

  // Loading  actions
  setLoading(isLoading: boolean) {
    this.state.update((state) => ({
      ...state,
      loading: isLoading,
    }));
  }

  // placeholder  actions
  setPlacehodler(placeHolders: PlaceHolder[]) {
    this.state.update((state) => ({
      ...state,
      placeHolders: placeHolders,
    }));
  }

  setCurrentMigrationId(currentMigrationId: string | undefined) {
    this.state.update((state) => ({
      ...state,
      currentMigrationId,
    }));
  }

  /* User data actions start */
  setUserData(userData: UserData) {
    this.state.update((state) => ({
      ...state,
      userData: {
        ...state.userData,
        ...userData,
        lastAccessed: new Date().toISOString(),
      },
    }));
  }

  updateUserPreference(key: string, value: any) {
    this.state.update((state) => ({
      ...state,
      userData: {
        ...state.userData,
        preferences: {
          ...(state.userData?.preferences || {}),
          [key]: value,
        },
      },
    }));
  }

  clearUserData() {
    this.state.update((state) => ({
      ...state,
      userData: {},
    }));
  }

  /* User data actions end */

  // Reset everything
  resetAllState() {
    this.state.set({
      loading: false,
      userData: {},
      migrationProgress: 0,
    });
  }
}
